import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { analyticsPath, pageView } from './lib/analytics';
import { CALLBACK_PATH } from './lib/oidc';
import { useSession } from './lib/session';
import { AuthCallbackPage } from './routes/AuthCallbackPage';
import { ChatPage } from './routes/ChatPage';
import { CreateFamilyPage } from './routes/CreateFamilyPage';
import { HelpPage } from './routes/HelpPage';
import { PrivacyPage } from './routes/PrivacyPage';
import { ProfileSelectPage } from './routes/ProfileSelectPage';
import { PuzzlePage } from './routes/PuzzlePage';
import { SetPasswordPage } from './routes/SetPasswordPage';
import { SignInPage } from './routes/SignInPage';
import { StartPage } from './routes/StartPage';
import { VerifyEmailPage } from './routes/VerifyEmailPage';
import { BillingPage } from './routes/parent/BillingPage';
import { ChildDetailPage } from './routes/parent/ChildDetailPage';
import { ChildrenPage } from './routes/parent/ChildrenPage';
import { DashboardLayout } from './routes/parent/DashboardLayout';
import { OverviewPage } from './routes/parent/OverviewPage';
import { ParentGate } from './routes/parent/ParentGate';
import { SecurityPage } from './routes/parent/SecurityPage';
import { SheetsPage } from './routes/parent/SheetsPage';
import { CurriculumPage } from './routes/parent/CurriculumPage';

// The two paths the server puts in its mail. They are declared here as well
// as in internal/identity/mail.go, and a change to either one has to be a
// change to both — a link that opens a page this app does not route is a dead
// end in somebody's inbox.
const VERIFY_PATH = '/adresse-bestaetigen';
const SET_PASSWORD_PATH = '/passwort-setzen';
const PRIVACY_PATH = '/datenschutz';

// Three doors, and which one is open depends on who is at the tablet.
//
// An unpaired device has one screen: the parent signs in and the device learns
// its family. After that the app boots to the profile picker every time, which
// is the daily flow — a child taps their name and four digits. Parent mode is
// behind the gear, and behind the parent's PIN.
//
// The two mailed links are mounted on both sides of that split. Somebody
// following one out of their inbox is not signed in, and may be doing it on a
// tablet that is already paired to the family — a second parent confirming
// their address, or anybody resetting a password.
export function App() {
  const { pairing, needsFamily, who } = useSession();
  const { pathname } = useLocation();

  // A parent's page views, and never a child's: lib/analytics.ts decides
  // which pages count and under what path.
  const paired = pairing !== null;
  useEffect(() => {
    pageView(analyticsPath({ pathname, paired, needsFamily, who }));
    // who.kind, not who: a new object with the same kind is the same page.
  }, [pathname, paired, needsFamily, who.kind]);

  if (!pairing) {
    return (
      <Routes>
        <Route path={CALLBACK_PATH} element={<AuthCallbackPage />} />
        <Route path={VERIFY_PATH} element={<VerifyEmailPage />} />
        <Route path={SET_PASSWORD_PATH} element={<SetPasswordPage />} />
        <Route path={PRIVACY_PATH} element={<PrivacyPage />} />
        {/* Signed in, but with no family to pair to yet: founding one is the
            next step, not another sign-in. */}
        <Route path="*" element={needsFamily ? <CreateFamilyPage /> : <SignInPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={CALLBACK_PATH} element={<AuthCallbackPage />} />
      <Route path={VERIFY_PATH} element={<VerifyEmailPage />} />
      <Route path={SET_PASSWORD_PATH} element={<SetPasswordPage />} />
      <Route path="/" element={who.kind === 'child' ? <StartPage /> : <ProfileSelectPage />} />
      <Route path="/hilfe" element={<HelpPage />} />
      <Route path={PRIVACY_PATH} element={<PrivacyPage />} />
      <Route
        path="/sitzung/:id"
        element={who.kind === 'child' ? <ChatPage /> : <Navigate to="/" replace />}
      />
      <Route
        path="/raetsel/:id"
        element={who.kind === 'child' ? <PuzzlePage /> : <Navigate to="/" replace />}
      />
      <Route path="/eltern" element={<ParentGate />}>
        <Route element={<DashboardLayout />}>
          <Route index element={<OverviewPage />} />
          <Route path="kinder" element={<ChildrenPage />} />
          <Route path="kinder/:id" element={<ChildDetailPage />} />
          <Route path="lehrplan" element={<CurriculumPage />} />
          <Route path="arbeitsblaetter" element={<SheetsPage />} />
          <Route path="abo" element={<BillingPage />} />
          <Route path="sicherheit" element={<SecurityPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
